import type { MouseEvent } from 'react'

import { useStore } from '@nanostores/react'
import clsx from 'clsx'
import { atom } from 'nanostores'
import * as filesUtils from 'shared/files'
import { useSwitcher } from 'shared/hooks'
import { typo } from 'shared/styles'
import { StackIcon } from 'shared/ui'

import * as files from '../../utils/files'
import { useGetReupload } from './hooks'
import css from './PreviewFiles.module.scss'
import { Error, recheckLoad } from './utils'

export const $shouldShowMultipleFilesError = atom(false)

export function PreviewFiles() {
	const ids = files.useIds()
	const showMultipleFiles = useStore($shouldShowMultipleFilesError)

	if (ids.size || showMultipleFiles) {
		return (
			<ul className={css.list}>
				{Array.from(ids, id => (
					<Thumbnail id={id} key={id} />
				))}

				{showMultipleFiles && <MultipleFileError />}
			</ul>
		)
	}
}

function MultipleFileError() {
	return (
		<li className={clsx(css.multiple, typo.buttonSmB)}>
			Only 1 file supported at a time. Please try again.
			<button
				className={'li-⚪'}
				onClick={() => $shouldShowMultipleFilesError.set(false)}
				type='button'
			>
				<StackIcon
					icon='close'
					label='Remove file'
					stroke='currentcolor'
					width={12}
				/>
			</button>
		</li>
	)
}

function Thumbnail({ id }: { id: string }) {
	const file = files.useFile(id)
	const loaded = recheckLoad(file)

	const hasError = Boolean(file.error)
	const isReady = !hasError && loaded === 100
	const isInProgress = !hasError && !isReady
	const hasReupload = file.error === Error.Upload
	const isImage = !hasError && file.type.startsWith('image')
	const isFile = !hasError && !isImage

	return (
		<li>
			<figure className={css.thumbnail}>
				{hasError && (
					<StackIcon className={css.errorIcon} icon='stop' stack='linkedin' />
				)}
				{isImage && (
					<img
						alt={`Preview ${file.name}`}
						className={css.pic}
						height={40}
						src={file.preview}
						width={40}
					/>
				)}

				{isFile && <StackIcon className={css.file} icon='file' />}

				<figcaption className={clsx(css.caption, typo.buttonSm)}>
					<h3 className={clsx(typo.buttonSmB, css.name)}>{file.name}</h3>
					<p className={css.size}>{filesUtils.formatSize(file.size)}</p>

					<p
						className={clsx(css.body, {
							[css.error]: hasError,
							[css.uploading]: isInProgress
						})}
					>
						{hasError && file.error}
						{isReady && 'Attached'}
						{isInProgress && [
							'Uploading',
							<progress
								className={css.progress}
								key='⬆️'
								max={100}
								value={loaded}
							/>
						]}
					</p>

					<div className={css.actions}>
						{hasReupload && <Reupload id={id} />}

						<button
							className={clsx(css.remove, 'li-⚪')}
							onClick={handleRemoveFile}
							type='button'
							value={id}
						>
							<StackIcon
								icon='close'
								label='Remove file'
								stroke='currentcolor'
								width={12}
							/>
						</button>
					</div>
				</figcaption>
			</figure>
		</li>
	)
}

const REUPLOAD_LABEL = 'Reupload File'

function handleRemoveFile(event: MouseEvent<HTMLButtonElement>) {
	const id = event.currentTarget.value

	files.remove(id)
}

function Reupload({ id }: { id: string }) {
	const loading = useSwitcher(false)

	const map = useGetReupload(id)
	const reupload = map[id]

	function uploadWithLoad() {
		loading.on()
		reupload?.().then(loading.off)
	}

	return (
		<button
			aria-label={REUPLOAD_LABEL}
			className={clsx('li-⚪', loading.isOn && css.uploadLoading)}
			onClick={uploadWithLoad}
			title={REUPLOAD_LABEL}
			type='button'
		>
			🗘
		</button>
	)
}
